/**
 * « À quelle demande cette proposition répond-elle ? » — chunk 9.AO.1.
 *
 * ## Pourquoi ce champ existe
 *
 * Le rattachement d'une proposition à son créneau assureur est le lien dont
 * dépendent la boucle assureur (9.AG.1), le filtre par demande du comparateur
 * (9.AH.1) et la réconciliation des demandes d'information (9.AN.1). Aucune des
 * quinze propositions du corpus ne le portait : le seul geste qui l'écrivait
 * n'était pas celui que les conseillers font. Ce champ met le lien là où le
 * conseiller se trouve déjà — au moment où il verse le document.
 *
 * ## Ce qu'il ne fait jamais
 *
 * **Choisir à la place du conseiller sans le dire.** Le couple
 * `(dossier, assureur)` ne désigne pas un créneau : huit couples en ont
 * plusieurs (9.AH.1). Quand un seul créneau reste ouvert pour l'assureur choisi,
 * on le pré-sélectionne — visiblement, dans un champ ouvert, avec une phrase qui
 * l'annonce et une option pour le retirer. Quand il y en a plusieurs, rien n'est
 * choisi : c'est au conseiller de trancher.
 *
 * Les créneaux d'une demande annulée ou expirée ne sont pas proposés — l'API les
 * refuse, et proposer ce qui sera refusé n'aide personne.
 */

import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

import type { InsurerLoop } from "../../../lib/queries/deadlines";

/** Une demande figée ne reçoit plus de réponse : ses créneaux ne sont pas offerts. */
const DEMANDES_FIGEES = new Set(["cancelled", "expired"]);

/** Un créneau décliné a reçu sa réponse — un refus est une réponse. */
const CRENEAUX_FERMES = new Set(["declined"]);

export type CreneauOption = {
  recipient_id: string;
  /** Titre de la demande, pour que le conseiller reconnaisse laquelle. */
  request_title: string | null;
  request_sent_at: string | null;
  /** Vrai quand la réponse a déjà été consignée à la main, sans document. */
  deja_consigne: boolean;
  /** Vrai pour le créneau auquel la proposition est déjà rattachée (9.AP.1). */
  est_courant: boolean;
};

/**
 * Les créneaux auxquels cette proposition PEUT répondre.
 *
 * Exporté et testé à part : c'est la règle, et une règle se vérifie sans rendre
 * un écran.
 */
export function creneauxDisponibles(
  boucle: InsurerLoop | undefined,
  insurerCode: string,
  /**
   * Le créneau auquel la proposition est DÉJÀ rattachée (9.AP.1).
   *
   * Il reste offert bien qu'il soit pourvu : c'est cette proposition-ci qui le
   * pourvoit. Sans cette exception, l'écran de reprise afficherait « aucune
   * demande » là où le rattachement existe, et le conseiller croirait devoir le
   * refaire.
   */
  creneauCourant?: string | null,
): CreneauOption[] {
  // `Array.isArray` et pas `?? []` : une réponse d'une autre forme (erreur
  // enveloppée, contrat changé) doit dégrader en « aucun créneau », pas faire
  // tomber l'écran de téléversement — leçon 9.AH.1.
  if (!insurerCode || !boucle || !Array.isArray(boucle.requests)) return [];
  const options: CreneauOption[] = [];
  for (const demande of boucle.requests) {
    if (DEMANDES_FIGEES.has(demande.status)) continue;
    if (!Array.isArray(demande.recipients)) continue;
    for (const d of demande.recipients) {
      if (d.insurer_code !== insurerCode) continue;
      const estCourant = creneauCourant != null && d.recipient_id === creneauCourant;
      // Déjà pourvu par une autre proposition : l'API refuserait, et l'unicité
      // en base aussi.
      if (!estCourant && d.submission_id !== null) continue;
      if (!estCourant && CRENEAUX_FERMES.has(d.status)) continue;
      options.push({
        recipient_id: d.recipient_id,
        request_title: demande.title,
        request_sent_at: demande.sent_at,
        deja_consigne: d.status === "responded",
        est_courant: estCourant,
      });
    }
  }
  return options;
}

export function RecipientSlotPicker({
  insurerCode,
  boucle,
  isLoading,
  value,
  onChange,
  disabled,
}: {
  insurerCode: string;
  boucle: InsurerLoop | undefined;
  isLoading: boolean;
  value: string;
  onChange: (recipientId: string) => void;
  disabled?: boolean;
}) {
  const { t, i18n } = useTranslation();

  const options = useMemo(() => creneauxDisponibles(boucle, insurerCode), [boucle, insurerCode]);
  const suggestion = options.length === 1 ? options[0]!.recipient_id : null;

  // Pré-sélection visible quand l'ambiguïté n'existe pas — UNE fois.
  //
  // Le garde-fou par référence est le cœur du geste : sans lui, un conseiller qui
  // repasse volontairement en « dépôt direct » verrait le créneau se remettre en
  // place tout seul, et n'aurait aucun moyen de refuser la suggestion. Une
  // suggestion qu'on ne peut pas décliner est une décision déguisée.
  const dejaSuggere = useRef<string | null>(null);
  useEffect(() => {
    if (!suggestion) return;
    // Déjà sur ce créneau — qu'on vienne de le suggérer ou que le formulaire
    // l'ouvre ainsi : on note que la suggestion a eu lieu, sinon un retour au
    // dépôt direct la ferait revenir.
    if (value === suggestion) {
      dejaSuggere.current = suggestion;
      return;
    }
    if (value === "" && dejaSuggere.current !== suggestion) {
      dejaSuggere.current = suggestion;
      onChange(suggestion);
    }
  }, [suggestion, value, onChange]);

  // Rien à proposer : pas de champ mort à l'écran. Le rattachement reste possible
  // plus tard, et l'absence de demande ouverte est un fait, pas une erreur.
  if (!insurerCode) return null;
  if (!isLoading && options.length === 0) return null;

  const dateFmt = new Intl.DateTimeFormat(i18n.resolvedLanguage ?? "fr", { dateStyle: "medium" });
  const libelle = (o: CreneauOption) => {
    const titre = o.request_title?.trim() || t("submissions.create.slot_untitled");
    const envoye = o.request_sent_at ? ` — ${dateFmt.format(new Date(o.request_sent_at))}` : "";
    const consigne = o.deja_consigne ? ` — ${t("submissions.create.slot_already_recorded")}` : "";
    return `${titre}${envoye}${consigne}`;
  };

  return (
    <div className="sm:col-span-2">
      <label htmlFor="responds_to_recipient_id" className="label">
        {t("submissions.create.slot_label")}
      </label>
      {isLoading ? (
        <p className="text-sm text-slate-500">{t("submissions.create.slot_loading")}</p>
      ) : (
        <>
          <select
            id="responds_to_recipient_id"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            aria-describedby="responds_to_recipient_id_hint"
            className="input"
          >
            <option value="">— {t("submissions.create.slot_none")} —</option>
            {options.map((o) => (
              <option key={o.recipient_id} value={o.recipient_id}>
                {libelle(o)}
              </option>
            ))}
          </select>
          <p id="responds_to_recipient_id_hint" className="mt-1 text-xs text-slate-500">
            {suggestion && value === suggestion
              ? t("submissions.create.slot_suggested_hint")
              : t("submissions.create.slot_hint")}
          </p>
        </>
      )}
    </div>
  );
}
